/**
 * Разбор и сравнение денежных сумм из полей ввода — общий для всех полей суммы
 * в долларах: личные минимумы, форма правила алерта, фильтры библиотеки и
 * «минимальный размер» фильтра экрана. Сокращения `к`/`м`/`кк` — §66 ТЗ 1.53.
 * Суммы — десятичные строки, `Number` не используется: крупная сумма не теряет
 * разряды, а дробь не превращается в двоичную.
 */

/** Разделители разрядов, которые поле суммы принимает и отбрасывает: пробел, U+00A0, U+202F, `_`. */
const AMOUNT_SEPARATORS = /[   _]/g;
/** Голое число: целое или целое с дробью через точку. */
const PLAIN_PATTERN = /^(\d+)(?:\.(\d+))?$/;
/** Число (дробь через точку или запятую) и буква-множитель. */
const SUFFIXED_PATTERN = /^(\d+)(?:[.,](\d+))?(kk|кк|k|к|m|м)$/;
/** Голая буква — сама единица. */
const BARE_SUFFIX_PATTERN = /^(kk|кк|k|к|m|м)$/;

/** Число нулей, на которое буква сдвигает точку: тысяча — 3, миллион — 6. */
const SUFFIX_SHIFT: Record<string, number> = {
  k: 3,
  к: 3,
  m: 6,
  м: 6,
  kk: 6,
  кк: 6,
};

/** Разделитель между двумя буквами множителя: «к к», «k_k» — двойная буква разорвана. */
const SPLIT_SUFFIX_PATTERN = /[kкmм][   _]+[kкmм]/;

export type AmountParseResult = { ok: true; value: string | null } | { ok: false };

/**
 * Сдвигает десятичную точку вправо на `shift` знаков в строке и нормализует:
 * без ведущих нулей целой части (но «0» остаётся), без хвостовых нулей дроби,
 * без пустой дроби.
 */
function shiftDecimal(integer: string, fraction: string, shift: number): string {
  const digits = integer + fraction.padEnd(shift, "0");
  const pointAt = integer.length + shift;
  const wholePart = digits.slice(0, pointAt).replace(/^0+(?=\d)/, "");
  const fractionPart = digits.slice(pointAt).replace(/0+$/, "");
  return fractionPart === "" ? wholePart : `${wholePart}.${fractionPart}`;
}

/**
 * Разбирает поле суммы по §66 ТЗ 1.53 (design.md stage8-amount-shorthand,
 * Д2–Д3). Убирает пробелы разрядов и `_`; пустое после очистки — `null`
 * (значит «любое», не ошибка). Дальше строка без учёта регистра должна целиком
 * совпасть с одной формой: целое, целое с дробью через точку, число (дробь
 * через точку или запятую) с буквой `к`/`м`/`кк` (латиница или кириллица), либо
 * голая буква. Запятая без буквы — ошибка: решение 27.09.2026 о запятой-разрядах
 * отменено §66. Умножение — сдвигом точки в строке, без `Number`.
 */
export function parseAmountField(raw: string): AmountParseResult {
  // Разделитель внутри двойной буквы нечитаем; пробел перед буквой допустим.
  if (SPLIT_SUFFIX_PATTERN.test(raw.toLowerCase())) {
    return { ok: false };
  }
  const cleaned = raw.replace(AMOUNT_SEPARATORS, "").toLowerCase();
  if (cleaned === "") {
    return { ok: true, value: null };
  }
  const plain = PLAIN_PATTERN.exec(cleaned);
  if (plain) {
    return { ok: true, value: shiftDecimal(plain[1] ?? "", plain[2] ?? "", 0) };
  }
  const suffixed = SUFFIXED_PATTERN.exec(cleaned);
  if (suffixed) {
    const shift = SUFFIX_SHIFT[suffixed[3] ?? ""];
    if (shift === undefined) {
      return { ok: false };
    }
    return { ok: true, value: shiftDecimal(suffixed[1] ?? "", suffixed[2] ?? "", shift) };
  }
  if (BARE_SUFFIX_PATTERN.test(cleaned)) {
    const shift = SUFFIX_SHIFT[cleaned];
    if (shift === undefined) {
      return { ok: false };
    }
    return { ok: true, value: shiftDecimal("1", "", shift) };
  }
  return { ok: false };
}

/** Сравнивает две уже очищенные десятичные строки `a <= b` через `BigInt`. */
export function amountLessOrEqual(a: string, b: string): boolean {
  const [aInt, aFrac = ""] = a.split(".");
  const [bInt, bFrac = ""] = b.split(".");
  const fracLength = Math.max(aFrac.length, bFrac.length);
  const aPadded = `${aInt}${aFrac.padEnd(fracLength, "0")}`;
  const bPadded = `${bInt}${bFrac.padEnd(fracLength, "0")}`;
  return BigInt(aPadded) <= BigInt(bPadded);
}
